import { useMemo, useState } from 'react';
import { palette } from './palette';
import { ChartBarsIcon, ChevronLeftIcon, ChevronRightIcon } from './icons';
import { byDayTaskCounts, startOfWeek, pacificWeekdayIndex } from './derive';
import type { EventLogEntry } from '@/lib/events-api';
import { DataPlaceholder } from './DataPlaceholder';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// `start` is a UTC-midnight anchor for a Pacific Monday (see startOfWeek), so the
// range is formatted in UTC to read the civil date back without a timezone shift.
function fmtRange(start: Date): string {
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return `${start.toLocaleDateString([], opts)} – ${end.toLocaleDateString([], opts)}`;
}

export function WeeklySummaryCard({ events }: { events: EventLogEntry[] }) {
  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week
  const today = useMemo(() => new Date(), []);
  const thisWeekStart = useMemo(() => startOfWeek(today), [today]);
  const weekStart = useMemo(() => {
    const d = new Date(thisWeekStart);
    d.setUTCDate(d.getUTCDate() - weekOffset * 7);
    return d;
  }, [thisWeekStart, weekOffset]);

  const counts = useMemo(() => byDayTaskCounts(events, weekStart), [events, weekStart]);
  const total = counts.reduce((a, b) => a + b, 0);
  const max = Math.max(1, ...counts);
  const todayDow = pacificWeekdayIndex(today);
  const todayIdx = weekOffset === 0 ? todayDow : -1;

  const label = weekOffset === 0 ? 'This Week' : weekOffset === 1 ? 'Last Week' : `${weekOffset} weeks ago`;
  const hasData = total > 0;

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-border bg-surface p-6 shadow-shell">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-white"
            style={{ background: palette.good }}
          >
            <ChartBarsIcon size={16} />
          </div>
          <div>
            <div className="text-[14px] font-extrabold tracking-[0.06em]">WEEKLY SUMMARY</div>
            <div className="mt-0.5 font-mono text-[11px] text-muted">{fmtRange(weekStart)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setWeekOffset(weekOffset + 1)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted transition-colors hover:text-foreground"
          >
            <ChevronLeftIcon />
          </button>
          <div className="min-w-[110px] rounded-lg border border-border px-3.5 py-1.5 text-center text-[12px] font-bold tracking-[0.06em]">
            {label}
          </div>
          <button
            onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted transition-colors hover:text-foreground disabled:opacity-30"
            disabled={weekOffset === 0}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <WeekStat label="TASKS COMPLETED" value={hasData ? total.toLocaleString() : null} accent />
        <WeekStat label="AVG CYCLE TIME" value={null} />
        <WeekStat label="OPERATIONAL HOURS" value={null} />
      </div>

      <div>
        <div className="mb-3.5 flex items-baseline justify-between">
          <div className="text-[10px] font-bold tracking-[0.14em] text-muted">
            TASKS COMPLETED · BY DAY
          </div>
          <div className="font-mono text-[10px]" style={{ color: palette.faint }}>
            count / day
          </div>
        </div>
        <div
          className="flex h-[130px] items-end gap-3.5 rounded-md px-2 py-2"
          style={{ background: palette.bgAlt }}
        >
          {counts.map((v, i) => {
            const h = (v / max) * 110;
            const isToday = i === todayIdx;
            return (
              <div key={i} className="flex flex-1 flex-col items-center gap-2 py-2.5">
                <div
                  className="font-mono text-[11px] font-bold tabular-nums"
                  style={{ color: isToday ? palette.goodInk : palette.muted }}
                >
                  {v}
                </div>
                <div
                  className="w-full rounded"
                  style={{
                    height: Math.max(h, 2),
                    background: isToday ? palette.good : '#d8d8d4',
                  }}
                />
                <div
                  className="text-[10px] font-bold tracking-[0.1em]"
                  style={{ color: isToday ? palette.goodInk : palette.muted }}
                >
                  {DAYS[i]}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekStat({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string | null;
  accent?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.14em] text-muted">{label}</div>
      <div
        className="mt-1.5 text-[26px] font-extrabold tracking-tight tabular-nums"
        style={accent && value != null ? { color: palette.goodInk } : undefined}
      >
        {value ?? <DataPlaceholder />}
      </div>
    </div>
  );
}
